/**
 * @module Components.Wrappers
 */
import { RoleType, UserContextType } from "app/contexts/UserContext";
import { hasFeature } from "app/utils/hasFeature";
import { RoleAccess } from "app/utils/roleAccess";

/** The access fields a RouterData entry may carry. */
export interface RouteAccessRule {
  /** A product feature the account must carry (with permissionObject "feature"). */
  permission?: string;
  permissionObject?: string;
  /**
   * A role rule from app/utils/roleAccess — the same predicate that hides the
   * page's menu entry, so a hidden page is not reachable by URL either.
   */
  access?: RoleAccess;
}

/**
 * Whether the signed-in account may open a route. The feature check is the
 * one PrivateRoute has always made, with its exception for facility admins
 * and schedulers on the scheduling pages (their layout applies the facility's
 * own flag). The role rule is CN-2813's.
 */
export const isRouteAllowed = (
  rule: RouteAccessRule,
  features: string[] | undefined,
  user: UserContextType,
): boolean => {
  if (
    rule.permissionObject === "feature" &&
    rule.permission &&
    !hasFeature(features, rule.permission)
  ) {
    const isAdminOrScheduler =
      user.behavesLikeAdmin || user.role === RoleType.SCHEDULER;
    if (!(rule.permission === "scheduling" && isAdminOrScheduler)) {
      return false;
    }
  }
  if (rule.access && !rule.access(user)) {
    return false;
  }
  return true;
};
